import { LanguagesEnum } from '@/api/enums';
import { equal, handleAxiosError } from '@/api/helpers';
import { AccountAdminTask, AdminTaskStatus } from '@/client/admin-tasks/types';
import { ADMIN_TASKS_VIEW } from '@/common/constants/tables';
import { LanguageContext } from '@/common/context/LanguageContext';
import {
  useAccountAdminTasks,
  useUpdateAccountAdminTask,
} from '@/hooks/query/account-admin-tasks.hooks';
import { useAppSelector } from '@/hooks/store';
import { useAccordionState } from '@/hooks/useAccordionState';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AccordionTabHeader } from '@/ui/accordion/AccordionTabHeader';
import { AppAccordion } from '@/ui/accordion/AppAccordion';
import { FlexContainer } from '@/ui/styled-ui';
import {
  getAdminTaskDetails,
  isTaskVisibleByFeatureFlag,
} from '@/utils/helpers';
import { AxiosError } from 'axios';
import { AccordionTab } from 'primereact/accordion';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { EmptyState } from './EmptyState';
import { TaskCard } from './TaskCard';
import { TaskTabs } from './TaskTabs';

const StyledBadgePill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--red-dark-opacity);
  color: var(--red-darkest);
  border-radius: var(--grand-border-radius);
  padding: var(--xxxsmall-padding) var(--xsmall-padding);
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  font-weight: 500;
  white-space: nowrap;
`;

const StyledContainer = styled(FlexContainer)`
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: var(--default-padding);
  width: 100%;

  h2 {
    font-size: var(--heading-small-font-size);
    margin: 0;
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const StyledTaskCardsWrapper = styled(FlexContainer)<{ $hasScroll: boolean }>`
  flex-direction: column;
  gap: var(--small-padding);
  transition: opacity 0.3s ease-out;
  width: 100%;
  align-items: flex-start;
  justify-content: flex-start;
  ${({ $hasScroll }) =>
    $hasScroll &&
    `
    max-height: 468px;
    overflow-y: auto;
    overflow-x: hidden;
  `}

  & > * {
    flex-shrink: 0;
    min-height: fit-content;
  }
`;

const StyledAccordionTab = styled(AppAccordion)`
  align-items: flex-start;
  justify-content: flex-start;
  gap: var(--default-padding);
  border: 1px solid var(--gray-dark);
  border-radius: var(--medium-border-radius);
  width: 100%;

  .p-accordion-tab {
    background-color: transparent !important;
    margin: 0;
  }

  .p-accordion-header-link {
    background-color: transparent !important;

    h2 {
      padding-bottom: 0;
    }
  }

  .p-accordion-header:hover {
    background-color: transparent !important;
  }
`;

export const AdminTasks: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  const account = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const toast = useToast();
  const { language } = useContext(LanguageContext);
  const activeIndexesKey = `${account?.id}-${currentUser?.id}`;
  const [activeTab, setActiveTab] = useState<AdminTaskStatus>(
    AdminTaskStatus.TODO,
  );
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const { accountCanUseFeature } = useFeatureFlag();

  const { accountAdminTasks, isLoading, refetch } = useAccountAdminTasks(
    account?.id as string,
    {
      filters: [equal('status', activeTab)],
      sort: [['task.order', 'asc'].join(',')],
    },
  );

  const { accountAdminTasks: todoCountData, refetch: refetchTodoCount } =
    useAccountAdminTasks(account?.id as string, {
      filters: [equal('status', AdminTaskStatus.TODO)],
    });

  const visibleTasks = useMemo(
    () =>
      accountAdminTasks?.result?.filter((task) =>
        isTaskVisibleByFeatureFlag(
          task,
          accountCanUseFeature,
          account?.featureFlagAbility,
        ),
      ),
    [accountAdminTasks?.result, account?.featureFlagAbility, account?.hubspot],
  );

  const todoCount = useMemo(() => {
    return activeTab === AdminTaskStatus.TODO && visibleTasks?.length
      ? visibleTasks.length
      : 0;
  }, [activeTab, visibleTasks?.length]);

  const { activeIndex, handleActiveIndexChange } = useAccordionState({
    storageKey: ADMIN_TASKS_VIEW,
    userKey: activeIndexesKey,
    defaultValue: 0,
    multiple: false,
    countTracking: {
      currentCount: todoCount,
      activeTab,
      targetTab: AdminTaskStatus.TODO,
    },
  });

  const maxButtonWidth = useMemo(() => {
    if (!visibleTasks?.length) return 120;

    const getButtonWidth = (text: string) => {
      const charWidth = 9;
      const padding = 24;
      return Math.max(text.length * charWidth + padding, 120);
    };

    let maxWidth = 120;

    visibleTasks.forEach((task: AccountAdminTask) => {
      const { buttonLabel } = getAdminTaskDetails(
        task,
        language as LanguagesEnum,
      );

      const buttonWidth = getButtonWidth(buttonLabel);
      const restoreWidth = getButtonWidth(t('generic.restore'));

      maxWidth = Math.max(
        maxWidth,
        task.status === AdminTaskStatus.TODO ? buttonWidth : restoreWidth,
      );
    });

    return maxWidth;
  }, [visibleTasks]);

  const handleTaskClick = (taskId: string) => {
    setExpandedTasks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const { update } = useUpdateAccountAdminTask();

  const handleTaskAction = async (
    task: AccountAdminTask,
    action: AdminTaskStatus,
  ) => {
    if (!account?.id || !task?.id) return;

    try {
      await update({
        id: task.id,
        status: action,
        accountId: account.id,
      });
      refetch();
      refetchTodoCount();
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  return (
    <StyledAccordionTab
      id="admin-tasks"
      className={className}
      activeIndex={activeIndex}
      onTabChange={handleActiveIndexChange}
    >
      <AccordionTab
        tabIndex={0}
        headerTemplate={
          <AccordionTabHeader
            titleComponent={
              <FlexContainer align="center" justify="flex-start" gap={8}>
                <h2 className="mb-0">{t('admin.tasks.title')}</h2>
                {!!todoCountData?.result?.length && (
                  <StyledBadgePill>
                    {t('admin.tasks.incomplete', {
                      count: todoCountData.result.length,
                    })}
                  </StyledBadgePill>
                )}
              </FlexContainer>
            }
          />
        }
      >
        <StyledContainer id="admin-tasks-container">
          <TaskTabs
            id="admin-tasks-tabs"
            activeTab={activeTab}
            onTabChange={(tab) => setActiveTab(tab)}
          />

          {isLoading && !visibleTasks?.length && (
            <EmptyState status={activeTab} isLoading />
          )}

          {!!visibleTasks?.length && !isLoading && (
            <StyledTaskCardsWrapper $hasScroll={visibleTasks.length > 5}>
              {visibleTasks.map((task: AccountAdminTask, index: number) => (
                <TaskCard
                  id={`admin-task-card-${task.task.key}-${task.status}`}
                  key={task.id}
                  adminTask={task}
                  expanded={expandedTasks.has(task?.id || '')}
                  onTaskClick={handleTaskClick}
                  onTaskAction={handleTaskAction}
                  style={{
                    animation: `fadeInUp 0.4s ease-out ${index * 0.1}s both`,
                  }}
                  maxButtonWidth={maxButtonWidth}
                />
              ))}
            </StyledTaskCardsWrapper>
          )}

          {!isLoading &&
            (!visibleTasks?.length || visibleTasks.length === 0) && (
              <EmptyState status={activeTab} />
            )}
        </StyledContainer>
      </AccordionTab>
    </StyledAccordionTab>
  );
};
